import React from 'react';
import Button from "components/Button";
import {ExclamationCircleIcon, PlusIcon, TrashIcon} from "@heroicons/react/solid";
import {FieldArray, FieldArrayRenderProps, FormikProps} from "formik";
import Divider from "components/Divider";
import {AudienceCriteriaGroup} from "types/AudienceCriteria";
import LocationFilter from "./Filters/LocationFilter";
import VisitFilter from "./Filters/VisitFilter";
import InteractionFilter from "./Filters/InteractionFilter";
import AttendedFilter from "./Filters/AttendedFilter";
import MembershipFilter from "./Filters/MembershipFilter";
import TicketFilter from "./Filters/TicketFilter";
import LoginFilter from "components/Form/CriteriaBuilder/Filters/LoginFilter";
import SponsorshipFilter from './Filters/SponsorshipFilter';
import RoleFilter from "./Filters/RoleFilter";
import AttributeFilter from "./Filters/AttributeFilter";
import FieldHelp from "../Form/Field/FieldHelp";
import PositionFilter from "./Filters/PositionFilter";

// Types ----

type CriteriaBuilderProps = {
  formik: FormikProps<any>
}

// Helpers ----

const groupTemplate: AudienceCriteriaGroup = {
  filters: {}
}

// Component ----

function CriteriaBuilder({formik}: CriteriaBuilderProps) {
  return (
    <FieldArray name="criteria.groups">
      {({push, remove}: FieldArrayRenderProps) => (
        <div>
          <ul>
            {formik.values.criteria?.groups.map((group: AudienceCriteriaGroup, groupIndex: number) => (
              <li key={groupIndex}>
                <div className="bg-gray-50 shadow-sm rounded-lg my-2">
                  <div className="px-4 py-3 sm:px-6 flex justify-between items-center">
                    <span className="font-medium">Group {groupIndex + 1}</span>

                    {formik.values.criteria.groups.length > 1 &&
                      <Button
                        color={Button.colors.WHITE}
                        size={Button.sizes.X_SMALL}
                        icon={TrashIcon}
                        onClick={() => remove(groupIndex)}
                      />
                    }
                  </div>

                  <div className="px-4 py-5 sm:p-6 text-sm">

                    {/* Location Filter */}
                    <LocationFilter
                      formik={formik}
                      groupIndex={groupIndex}
                      filter={group.filters.location}
                    />

                    {/* Visit Filter */}
                    <VisitFilter
                      formik={formik}
                      groupIndex={groupIndex}
                      filter={group.filters.visit}
                    />

                    {/* Position Filter */}
                    <PositionFilter
                      formik={formik}
                      groupIndex={groupIndex}
                      filter={group.filters.position}
                    />

                    {/* Interaction Filter */}
                    <InteractionFilter
                      formik={formik}
                      groupIndex={groupIndex}
                      filter={group.filters.interaction}
                    />

                    {/* Attended Filter */}
                    <AttendedFilter
                      formik={formik}
                      groupIndex={groupIndex}
                      filter={group.filters.attendance}
                    />

                    {/* Ticket Filter */}
                    <TicketFilter
                      formik={formik}
                      groupIndex={groupIndex}
                      filter={group.filters.ticket}
                    />

                    {/* Membership Filter */}
                    <MembershipFilter
                      formik={formik}
                      groupIndex={groupIndex}
                      filter={group.filters.membership}
                    />

                    {/* Sponsorship Filter */}
                    <SponsorshipFilter
                      formik={formik}
                      groupIndex={groupIndex}
                      filter={group.filters.sponsorship}
                    />

                    {/* First Login Filter */}
                    <LoginFilter
                      formik={formik}
                      groupIndex={groupIndex}
                      filter={group.filters.login}
                    />

                    {/* Role Filter */}
                    <RoleFilter
                      formik={formik}
                      groupIndex={groupIndex}
                      filter={group.filters.role}
                    />

                    {/* Attribute Filter */}
                    <AttributeFilter
                      formik={formik}
                      groupIndex={groupIndex}
                      filter={group.filters.attribute}
                    />

                    {/* @ts-ignore */}
                    {(!!formik.errors.criteria?.groups?.[groupIndex]?.filters) &&
                      <FieldHelp isError>
                        <div className="flex items-center gap-2 mt-8">
                          <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true"/>
                          {/* @ts-ignore */}
                          {formik.errors.criteria?.groups?.[groupIndex]?.filters}
                        </div>
                      </FieldHelp>
                    }
                  </div>
                </div>

                {(groupIndex !== formik.values.criteria.groups.length - 1) && <Divider label="OR"/>}
              </li>
            ))}
          </ul>

          <div className="mt-4">
            <Button
              color={Button.colors.SECONDARY}
              icon={PlusIcon}
              onClick={() => push({...groupTemplate})}
            >
              Add Group
            </Button>
          </div>
        </div>
      )}
    </FieldArray>
  );
}

// Export ----

export default CriteriaBuilder;